import { AdsPlacement } from 'types/enums/AdsPlacement';
import { AdsPlatform } from 'types/enums/AdsPlatform';
import { ChatAds } from 'types/interfaces/ads/ChatAds';
import { FinderDoubleVerticalAds } from 'types/interfaces/ads/FinderDoubleVerticalAds';
import { FinderHorizontalAds } from 'types/interfaces/ads/FinderHorizontalAds';
import { FinderVerticalAds } from 'types/interfaces/ads/FinderVerticalAds';
import { FloatingBubbleAds } from 'types/interfaces/ads/FloatingBubbleAds';
import { InterstitialAds } from 'types/interfaces/ads/InterstitialAds';
import { NotificationsHorizontalAds } from 'types/interfaces/ads/NotificationsHorizontalAds';
import { PageVerticalAds } from 'types/interfaces/ads/PageVerticalAds';
import { PicInPicAds } from 'types/interfaces/ads/PicInPicAds';
import { PopupAds } from 'types/interfaces/ads/PopupAds';
import { Qualification } from 'types/interfaces/ads/Qualification';
import { ThreadsAds } from 'types/interfaces/ads/ThreadsAds';
import { Package } from 'types/interfaces/Package';
import { AuthUser } from 'types/interfaces/user/AuthUser';

import { httpClient } from './httpClient';

interface UserTransferLink {
  url: string;
}
export interface PartnerOfferPopup {
  popup_url: string;
}

export interface TransferAuthWithTokenPayload {
  token: string;
  upsalePackageIds: string[] | null;
}
export interface TransferAuthWithTokenSuccessResponse {
  user: AuthUser;
}
export interface TransferAuthWithTokenFailResponse {
  user: AuthUser;
  to_host: string;
  login_token: string;
}

export interface TransferAuthParamsResponse {
  trial_duration: number;
  recurrent_price: number;
  recurrent_price_text: string;
  recurrent_duration: number;
  recurrent_duration_text: string;
  currency: string;
  descriptor: string;
  card_mask: string;
  upsale_packages: Package[];
  is_carousel_transfer_paywall_enabled: boolean;
}

export const UserTransferApi = {
  async fetchFinderHorizontalBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<FinderHorizontalAds>(
      `/ads/banner/${AdsPlacement.FinderHorizontal}?platform=${platform}`
    );

    return data;
  },

  async fetchFinderVerticalBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<FinderVerticalAds>(
      `/ads/banner/${AdsPlacement.FinderVertical}?platform=${platform}`
    );

    return data;
  },

  async fetchFinderDoubleVerticalBanner({
    platform,
  }: {
    platform: AdsPlatform;
  }) {
    const { data } = await httpClient.get<FinderDoubleVerticalAds>(
      `/ads/banner/${AdsPlacement.FinderDoubleVertical}?platform=${platform}`
    );

    return data;
  },

  async fetchPageVerticalBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<PageVerticalAds>(
      `/ads/banner/${AdsPlacement.MyProfileHorizontal}?platform=${platform}`
    );

    return data;
  },

  async fetchDialogsListBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<ThreadsAds>(
      `/ads/banner/${AdsPlacement.Threads}?platform=${platform}`
    );

    return data;
  },

  async fetchDialogBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<ChatAds>(
      `/ads/banner/${AdsPlacement.Chat}?platform=${platform}`
    );

    return data;
  },

  async fetchFinderPicInPicBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<PicInPicAds>(
      `/ads/banner/${AdsPlacement.PicInPic}?platform=${platform}`
    );

    return data;
  },

  async fetchVideoPopupBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<PopupAds>(
      `/ads/banner/${AdsPlacement.AdVideoPopup}?platform=${platform}`
    );

    return data;
  },

  async fetchFloatingBubbleBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<FloatingBubbleAds>(
      `/ads/banner/${AdsPlacement.FloatingBubble}?platform=${platform}`
    );

    return data;
  },

  async fetchNotificationsHorizontalBanner({
    platform,
  }: {
    platform: AdsPlatform;
  }) {
    const { data } = await httpClient.get<NotificationsHorizontalAds>(
      `/ads/banner/${AdsPlacement.NotificationsHorizontal}?platform=${platform}`
    );

    return data;
  },

  async fetchInterstitialBanner({ platform }: { platform: AdsPlatform }) {
    const { data } = await httpClient.get<InterstitialAds>(
      `/ads/banner/${AdsPlacement.Interstitial}?platform=${platform}`
    );

    return data;
  },

  async fetchUserTransferLink(): Promise<UserTransferLink> {
    const { data } = await httpClient.get('/ads/link/internal');
    return data;
  },

  async sendTransferAuthToken(payload: TransferAuthWithTokenPayload) {
    const { data } =
      await httpClient.post<TransferAuthWithTokenSuccessResponse>(
        `/users/transfer/${payload.token}`,
        {
          extra_package_infos: payload.upsalePackageIds,
        }
      );

    return data;
  },

  async fetchTransferAuthParams(token: string) {
    const { data } = await httpClient.get<TransferAuthParamsResponse>(
      `/users/transfer/one-click-auth-params/${token}`
    );

    return data;
  },

  async fetchQualification() {
    const { data } = await httpClient.get<Qualification>(`/user/qualification`);

    return data;
  },

  async fetchPartnerOfferPopup(): Promise<PartnerOfferPopup> {
    const { data } = await httpClient.get('/partner-offer/show');
    return data;
  },
};
